import {
  Box,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import UploadFiles from "components/common/UploadFiles/UploadFiles";
import { useSnackbar } from "components/common/Notification/showSnackbar";
import {
  useStartIngestFullIngestFullPost,
  useStartIngestNumericalIngestNumericalPost,
} from "orval/generated/endpoint";
import { IngestViewModel } from "orval/generated/models";
import { CustomIcon, Icons } from "components/common";

type IngestType = "full" | "numerical" | "";

interface UploadIngestModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ingestData?: IngestViewModel[];
}

function UploadIngestModal({ open, onClose, onSuccess, ingestData }: UploadIngestModalProps) {
  const showSnackbar = useSnackbar();
  
  const [files, setFiles] = useState<File[]>([]);
  const [ingestType, setIngestType] = useState<IngestType>("");

  // Check if numerical ingest should be disabled
  const isNumericalDisabled = () => {
    if (!ingestData || ingestData.length === 0) return false;
    
    // Get the most recent ingest (first in the chronologically sorted array)
    const lastIngest = ingestData[0];
    
    // Disable numerical if the last ingest was a failed full ingest
    return lastIngest.ingest_type === "full" && lastIngest.status === "failed";
  };

  const numericalDisabledReason = "After a failed full ingest, only full ingests are allowed. The previous full ingest cleared the data, so a numerical ingest cannot be performed.";
  
  // Reset ingest type if numerical is disabled and currently selected
  if (isNumericalDisabled() && ingestType === "numerical") {
    setIngestType("");
  }
  
  const startFullIngest = useStartIngestFullIngestFullPost();
  const startNumericalIngest = useStartIngestNumericalIngestNumericalPost();

  const renderTooltipContent = (items: string[]) => (
    <Box>
      {items.map((item, index) => (
        <div key={`${item}-${index}`}>
          {items.length > 1 ? `• ${item}` : item}
        </div>
      ))}
    </Box>
  );
  
  const isLoading = startFullIngest.isPending || startNumericalIngest.isPending;

  const handleSubmit = async () => {
    if (!files.length || !ingestType) {
      showSnackbar("Please select a file and ingest type", "error");
      return;
    }

    const zipFile = files[0];
    
    try {
      if (ingestType === "full") {
        await startFullIngest.mutateAsync({
          data: { zip: zipFile }
        });
        showSnackbar(
          "Full ingest started successfully",
          "success"
        );
      } else if (ingestType === "numerical") {
        await startNumericalIngest.mutateAsync({
          data: { zip: zipFile }
        });
        showSnackbar(
          "Numerical ingest started successfully",
          "success"
        );
      }
      
      // Reset form and close modal
      handleReset();
      onClose();
      
      // Trigger refresh of version history
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Ingest failed:", error);
      
      // Extract error message from backend response
      let errorMessage = "Failed to start ingest. Please try again.";
      
      if (error?.response?.data) {
        // Handle different error response formats
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showSnackbar(errorMessage, "error");
    }
  };

  const handleReset = () => {
    setFiles([]);
    setIngestType("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
          minHeight: "500px"
        }
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight="700" sx={{ fontSize: "1.75rem" }}>
            Start new ingest
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: "#666" }}>
            <CustomIcon
              name={Icons.CLOSE}
              width="20px"
              height="20px"
              fill="currentColor"
            />
          </IconButton>
        </Box>

        {/* Two Column Layout */}
        <Box display="flex" gap={4} minHeight="350px">
          {/* Left Column - Configuration */}
          <Box flex={1} pr={2}>
            {/* Ingest Type Selection */}
            <Box mb={4}>
              <Typography variant="body2" color="textSecondary" mb={3}>
                Select ingest type
              </Typography>
              <RadioGroup
                value={ingestType}
                onChange={(e) => setIngestType(e.target.value as IngestType)}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" />}
              label={
                <Box display="flex" alignItems="center">
                  <CustomIcon
                    name={Icons.DATABASE}
                    width="20px"
                    height="20px"
                    fill="#d04a02"
                  />
                  <Box ml={2}>
                    <Typography variant="body2" fontWeight="500">
                      Full ingest
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Complete dataset replacement
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ 
                border: "1px solid #e0e0e0", 
                borderRadius: 1, 
                p: 1.5, 
                m: 0,
                "&:hover": { backgroundColor: "#f5f5f5" }
              }}
            />
            <Tooltip 
              title={isNumericalDisabled() ? numericalDisabledReason : ""} 
              placement="top"
              arrow
            >
              <Box sx={{ width: "100%" }}>
                <FormControlLabel
                  value="numerical"
                  control={<Radio size="small" />}
                  disabled={isNumericalDisabled()}
                  label={
                    <Box display="flex" alignItems="center">
                      <CustomIcon
                        name={Icons.EDIT}
                        width="20px"
                        height="20px"
                        fill={isNumericalDisabled() ? "#ccc" : "#1976d2"}
                      />
                      <Box ml={2}>
                        <Typography 
                          variant="body2" 
                          fontWeight="500"
                          sx={{ color: isNumericalDisabled() ? "#ccc" : "textSecondary" }}
                        >
                          Numerical ingest
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ color: isNumericalDisabled() ? "#ccc" : "textSecondary" }}
                        >
                          Update numerical data only
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ 
                    border: `1px solid ${isNumericalDisabled() ? "#f0f0f0" : "#e0e0e0"}`, 
                    borderRadius: 1, 
                    p: 1.5, 
                    m: 0,
                    width: "100%",
                    backgroundColor: isNumericalDisabled() ? "#f9f9f9" : "transparent",
                    "&:hover": { 
                      backgroundColor: isNumericalDisabled() ? "#f9f9f9" : "#f5f5f5" 
                    },
                    cursor: isNumericalDisabled() ? "not-allowed" : "pointer"
                  }}
                />
              </Box>
            </Tooltip>
              </RadioGroup>
            </Box>

            {/* File Requirements */}
            <Box
              p={3}
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 1,
                border: "1px solid #e9ecef",
                height: "fit-content",
                minHeight: "320px",
                transition: "background-color 0.2s ease",
                opacity: ingestType ? 1 : 0.6,
                display: "flex",
                alignItems: ingestType ? "flex-start" : "center",
                justifyContent: "center",
                textAlign: ingestType ? "left" : "center",
              }}
            >
              {!ingestType ? (
                <Box>
                  <Typography variant="body2" fontWeight={500} mb={1}>
                    Select an ingest type
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Required files will appear here once you choose an ingest type.
                  </Typography>
                </Box>
              ) : (
                <Box width="100%">
                  <Box display="flex" alignItems="center" mb={2}>
                    <CustomIcon
                      name={ingestType === "full" ? Icons.DATABASE : Icons.EDIT}
                      width="16px"
                      height="16px"
                      fill="#666"
                    />
                    <Typography variant="body2" fontWeight="500" ml={1}>
                      Required files for {ingestType} ingest:
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ m: 0, pl: 2, fontSize: "0.8rem", color: "#666", lineHeight: 1.6 }}>
                    {ingestType === "full" ? (
                      <>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Overview CSV file
                        <Tooltip 
                          title={renderTooltipContent(["overview.csv"])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                    <li>Full dataset - excel file (.xlsx)</li>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Phase 1 CSV files
                        <Tooltip 
                          title={renderTooltipContent([
                            "country_index.csv",
                            "year_index.csv",
                            "category_index.csv",
                            "brand_group_index.csv",
                            "ingredient_index.csv",
                            "pack_type_index.csv",
                            "pack_size_index.csv",
                            "multipack_size_index.csv",
                            "material_index.csv",
                            "energy_type_source_index.csv",
                            "vehicle_index.csv",
                            "cde_class_index.csv",
                          ])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Phase 2 CSV files
                        <Tooltip 
                          title={renderTooltipContent([
                            "packaging_index.csv",
                            "part_material_index.csv",
                            "brand_index.csv",
                            "cde_group_index.csv",
                          ])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Phase 3 CSV files
                        <Tooltip 
                          title={renderTooltipContent([
                            "cde_unit_index.csv",
                            "sku.csv",
                          ])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Phase 4 CSV files
                        <Tooltip 
                          title={renderTooltipContent([
                            "brand_ingredient_spec.csv",
                            "ingredient_ef.csv",
                            "sku_packaging_spec.csv",
                            "packaging_ef.csv",
                            "sku_energy_use_ratio.csv",
                            "energy.csv",
                            "sku_distance_travelled.csv",
                            "vehicle_road.csv",
                            "vehicle_forklift.csv",
                            "vehicle_other.csv",
                            "cde_fleet.csv",
                          ])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                      </>
                    ) : (
                      <>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Overview CSV file
                        <Tooltip 
                          title={renderTooltipContent(["overview.csv"])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                    <li>Full dataset - excel file (.xlsx)</li>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        SKU CSV file
                        <Tooltip 
                          title={renderTooltipContent(["sku.csv"])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                    <li>
                      <Box display="inline-flex" alignItems="center">
                        Phase 4 CSV files
                        <Tooltip 
                          title={renderTooltipContent([
                            "brand_ingredient_spec.csv",
                            "ingredient_ef.csv",
                            "sku_packaging_spec.csv",
                            "packaging_ef.csv",
                            "sku_energy_use_ratio.csv",
                            "energy.csv",
                            "sku_distance_travelled.csv",
                            "vehicle_road.csv",
                            "vehicle_forklift.csv",
                            "vehicle_other.csv",
                            "cde_fleet.csv",
                          ])}
                          placement="top"
                        >
                          <Box ml={0.5} display="flex" alignItems="center">
                            <CustomIcon
                              name={Icons.INFORMATION}
                              width="12px"
                              height="12px"
                              fill="#999"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </li>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Vertical Divider */}
          <Box
            sx={{
              width: "1px",
              backgroundColor: "#e0e0e0",
              mx: 2
            }}
          />

          {/* Right Column - File Upload */}
          <Box flex={1} pl={2}>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Upload ZIP file
            </Typography>
            <Box sx={{ height: "100%" }}>
              <UploadFiles
                files={files}
                setFiles={setFiles}
                validMIMEFileTypes={[
                  "application/zip",
                  "application/x-zip-compressed"
                ]}
                multiple={false}
                maxFilesNumber={1}
                maxFileSize={100}
              />
            </Box>
          </Box>
        </Box>


        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={6}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={isLoading || (!files.length && !ingestType)}
            size="small"
            sx={{ px: 3 }}
          >
            Reset
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading || !files.length || !ingestType}
            size="small"
            sx={{ px: 3 }}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isLoading ? "Starting..." : "Start ingest"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default UploadIngestModal;
